import { useEffect } from 'react';
import type { LiveLinkState } from 'src/pages/Settings/memberships/components/live-link';

const RETRY_MS = 15_000;

/** A query a live link depends on. */
export interface LiveLinkLookup {
  loading: boolean;
  error?: unknown;
  refetch: () => Promise<unknown>;
}

/** For a bar whose page has nothing of its own to look up. */
export const IDLE_LOOKUP: LiveLinkLookup = {
  loading: false,
  refetch: () => Promise.resolve(),
};

// The error surfaces through the lookup itself, so the rejected promise has nothing to add.
const retry = (refetch: LiveLinkLookup['refetch']) => refetch().catch(() => undefined);

// Chained on the refetch rather than the error: Apollo keeps the same error object when an
// attempt fails the same way, so an effect keyed on it alone would try only once.
const useRetryOnError = ({ error, refetch }: LiveLinkLookup) => {
  useEffect(() => {
    if (!error) return;

    let timer: ReturnType<typeof setTimeout>;
    let active = true;
    const schedule = () => {
      timer = setTimeout(() => {
        refetch().catch(() => {
          if (active) schedule();
        });
      }, RETRY_MS);
    };
    schedule();

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [error, refetch]);
};

/**
 * What a live link bar says about `url`. A failed lookup is not proof the page is unpublished, so
 * the bar says it couldn't check, and tries again on its own, rather than reading "Not live".
 */
export const useLiveLinkState = (
  url: string | undefined,
  addressLookup: LiveLinkLookup,
  pageLookup: LiveLinkLookup,
): LiveLinkState => {
  useRetryOnError(addressLookup);
  useRetryOnError(pageLookup);

  const failed = [addressLookup, pageLookup].filter((lookup) => lookup.error);
  if (failed.length > 0) {
    return {
      status: 'failed',
      retry: () => failed.forEach((lookup) => retry(lookup.refetch)),
    };
  }
  if (addressLookup.loading || pageLookup.loading) return { status: 'checking' };

  return url ? { status: 'live', url } : { status: 'notLive' };
};
